import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as organisationService from '../../../services/organisationService';
import { getOrgId } from '../../../services/orgStore';
import { apiUrl } from '../../../utils/apiBase';
import { quickBooksAuthHeaders } from '../../../utils/quickBooksAuth';
import { useQuickBooksStatus } from '../../../context/QuickBooksStatusContext';
import { useXeroStatus } from '../../../context/XeroStatusContext';
import TermsAndConditionsSection from './TermsAndConditionsSection';

const FIELD_GROUPS = [
  {
    title: 'Organisation',
    fields: [
      { key: 'name', label: 'Organisation name' },
      { key: 'slug', label: 'Slug' },
      { key: 'abn', label: 'ABN' },
      { key: 'phone', label: 'Phone' },
    ],
  },
  {
    title: 'Address',
    fields: [
      { key: 'street', label: 'Street' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'postcode', label: 'Postcode' },
      { key: 'country', label: 'Country' },
    ],
  },
  {
    title: 'Emails',
    fields: [
      { key: 'sales_email', label: 'Sales email' },
      { key: 'purchase_email', label: 'Purchase email' },
    ],
  },
  // {
  //   title: 'Regional',
  //   fields: [
  //     { key: 'currency', label: 'Currency' },
  //     { key: 'industry', label: 'Industry' },
  //     { key: 'fiscal_year_start', label: 'Fiscal year start (month number)' },
  //   ],
  // },
];

const toInputValue = (value) => (value === null || value === undefined ? '' : String(value));

export default function OrganisationSettingsView() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const { loading: qbLoading, connected: qbConnected, error: qbStatusError, refresh: refreshQuickBooksStatus } =
    useQuickBooksStatus();
  const {
    loading: xeroLoading,
    connected: xeroConnected,
    tenantName: xeroTenantName,
    error: xeroStatusError,
    refresh: refreshXeroStatus,
  } = useXeroStatus();

  const dirty = useMemo(() => {
    if (!org) return false;
    return Object.keys(form).some((k) => {
      const a = toInputValue(form[k]);
      const b = toInputValue(org[k]);
      return a !== b;
    });
  }, [form, org]);

  const load = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const data = await organisationService.fetchOrganisation();
      setOrg(data);

      const initial = {};
      FIELD_GROUPS.forEach((g) => {
        g.fields.forEach((f) => {
          initial[f.key] = toInputValue(data?.[f.key]);
        });
      });
      setForm(initial);
    } catch (e) {
      console.error('Failed to load organisation:', e);
      setError(e?.message || 'Failed to load organisation.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startQuickBooksConnect = useCallback(async () => {
    const orgId = getOrgId();
    if (!orgId) {
      alert('No organisation selected.');
      return;
    }
    const auth = await quickBooksAuthHeaders();
    if (!auth.Authorization) {
      alert('You must be signed in to connect QuickBooks.');
      return;
    }
    try {
      const res = await fetch(apiUrl('/api/quickbooks/connect'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        credentials: 'include',
        body: JSON.stringify({ org_id: orgId }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) {
        throw new Error(
          data.error ||
            (text && !text.startsWith('{') ? text.slice(0, 200).trim() : '') ||
            `Could not start QuickBooks connection (HTTP ${res.status}). Is the API server running on port 5001, and is SUPABASE_ANON_KEY set in its .env?`
        );
      }
      if (!data.url) throw new Error('Invalid response from server');
      window.location.href = data.url;
    } catch (e) {
      alert(e.message || 'Failed to connect');
    }
  }, []);

  const disconnectQuickBooks = useCallback(async () => {
    if (!window.confirm('Disconnect QuickBooks for your account in this organisation?')) return;
    const orgId = getOrgId();
    if (!orgId) {
      alert('No organisation selected.');
      return;
    }
    const auth = await quickBooksAuthHeaders();
    if (!auth.Authorization) {
      alert('You must be signed in.');
      return;
    }
    try {
      const res = await fetch(apiUrl('/api/quickbooks/disconnect'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        credentials: 'include',
        body: JSON.stringify({ org_id: orgId }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) {
        throw new Error(
          data.error ||
            (text && !text.startsWith('{') ? text.slice(0, 200).trim() : '') ||
            `Disconnect failed (HTTP ${res.status})`
        );
      }
      await refreshQuickBooksStatus();
    } catch (e) {
      alert(e.message || 'Disconnect failed');
    }
  }, [refreshQuickBooksStatus]);

  const startXeroConnect = useCallback(async () => {
    const orgId = getOrgId();
    if (!orgId) {
      alert('No organisation selected.');
      return;
    }
    const auth = await quickBooksAuthHeaders();
    if (!auth.Authorization) {
      alert('You must be signed in to connect Xero.');
      return;
    }
    try {
      const res = await fetch(apiUrl('/api/xero/connect'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        credentials: 'include',
        body: JSON.stringify({ org_id: orgId }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) {
        throw new Error(
          data.error ||
            (text && !text.startsWith('{') ? text.slice(0, 200).trim() : '') ||
            `Could not start Xero connection (HTTP ${res.status}). Is the API server running on port 5001, and is XERO_CLIENT_ID set in its .env?`
        );
      }
      if (!data.url) throw new Error('Invalid response from server');
      window.location.href = data.url;
    } catch (e) {
      alert(e.message || 'Failed to connect');
    }
  }, []);

  const disconnectXero = useCallback(async () => {
    if (!window.confirm('Disconnect Xero for your account in this organisation?')) return;
    const orgId = getOrgId();
    if (!orgId) {
      alert('No organisation selected.');
      return;
    }
    const auth = await quickBooksAuthHeaders();
    if (!auth.Authorization) {
      alert('You must be signed in.');
      return;
    }
    try {
      const res = await fetch(apiUrl('/api/xero/disconnect'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        credentials: 'include',
        body: JSON.stringify({ org_id: orgId }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) {
        throw new Error(
          data.error ||
            (text && !text.startsWith('{') ? text.slice(0, 200).trim() : '') ||
            `Disconnect failed (HTTP ${res.status})`
        );
      }
      await refreshXeroStatus();
    } catch (e) {
      alert(e.message || 'Disconnect failed');
    }
  }, [refreshXeroStatus]);

  const onChange = (key) => (e) => {
    const next = e.target.value;
    setForm((prev) => ({ ...prev, [key]: next }));
  };

  const onSave = async () => {
    try {
      setError('');
      setSaving(true);

      const payload = { ...form };
      if ('fiscal_year_start' in payload) {
        const raw = payload.fiscal_year_start.trim();
        payload.fiscal_year_start = raw === '' ? null : Number(raw);
        if (payload.fiscal_year_start !== null && Number.isNaN(payload.fiscal_year_start)) {
          throw new Error('Fiscal year start must be a number (1-12).');
        }
      }

      // Convert empty strings to null for nullable columns
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') payload[k] = null;
      });

      const updated = await organisationService.updateOrganisation(payload);
      setOrg(updated);

      const nextForm = {};
      FIELD_GROUPS.forEach((g) => {
        g.fields.forEach((f) => {
          nextForm[f.key] = toInputValue(updated?.[f.key]);
        });
      });
      setForm(nextForm);
    } catch (e) {
      console.error('Failed to save organisation:', e);
      setError(e?.message || 'Failed to save organisation.');
      alert('Failed to save: ' + (e?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center mt-10">Loading...</div>;
  }

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-4xl">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-lg font-semibold text-gray-900">Settings</div>
            <div className="text-sm text-gray-600">
              Set up your organisation details used across invoices, emails, and reports.
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={load}
              className="px-3 py-2 bg-white text-gray-700 border border-gray-200 rounded-md cursor-pointer text-sm font-medium hover:bg-gray-50 transition-colors"
              disabled={saving}
            >
              Reload
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md cursor-pointer text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={saving || !dirty}
              title={!dirty ? 'No changes to save' : ''}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-900 mb-1">Accounting integrations</div>
          <p className="text-xs text-gray-600 mb-4">
            Connections are per user. Export invoices and bills from the Invoices and Bills screens.
          </p>

          <div className="mb-4 pb-4 border-b border-gray-100 last:mb-0 last:pb-0 last:border-b-0">
            <div className="text-sm font-medium text-gray-900 mb-1">QuickBooks Online</div>
            {qbLoading ? (
              <p className="text-sm text-gray-500">Checking connection…</p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`text-sm font-medium ${qbConnected ? 'text-emerald-700' : 'text-amber-700'}`}
                >
                  {qbConnected ? 'Connected' : 'Not connected'}
                </span>
                <button
                  type="button"
                  onClick={startQuickBooksConnect}
                  className="px-2 py-1 bg-[#2ca01c] text-white rounded-md text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer"
                >
                  {qbConnected ? 'Reconnect QuickBooks' : 'Connect QuickBooks'}
                </button>
                {qbConnected && (
                  <button
                    type="button"
                    onClick={disconnectQuickBooks}
                    className="px-2 py-1 bg-white text-red-700 border border-red-200 rounded-md text-sm font-medium hover:bg-red-50 cursor-pointer"
                  >
                    Disconnect
                  </button>
                )}
                <button
                  type="button"
                  onClick={refreshQuickBooksStatus}
                  className="px-2 py-1 bg-white text-gray-700 border border-gray-200 rounded-md text-sm cursor-pointer hover:bg-gray-50"
                >
                  Refresh status
                </button>
              </div>
            )}
            {qbStatusError && (
              <p className="mt-2 text-xs text-amber-800">{qbStatusError}</p>
            )}
          </div>

          <div>
            <div className="text-sm font-medium text-gray-900 mb-1">Xero</div>
            {xeroLoading ? (
              <p className="text-sm text-gray-500">Checking connection…</p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`text-sm font-medium ${xeroConnected ? 'text-emerald-700' : 'text-amber-700'}`}
                >
                  {xeroConnected
                    ? `Connected${xeroTenantName ? ` — ${xeroTenantName}` : ''}`
                    : 'Not connected'}
                </span>
                <button
                  type="button"
                  onClick={startXeroConnect}
                  className="px-2 py-1 bg-[#13B5EA] text-white rounded-md text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer"
                >
                  {xeroConnected ? 'Reconnect Xero' : 'Connect Xero'}
                </button>
                {xeroConnected && (
                  <button
                    type="button"
                    onClick={disconnectXero}
                    className="px-2 py-1 bg-white text-red-700 border border-red-200 rounded-md text-sm font-medium hover:bg-red-50 cursor-pointer"
                  >
                    Disconnect
                  </button>
                )}
                <button
                  type="button"
                  onClick={refreshXeroStatus}
                  className="px-2 py-1 bg-white text-gray-700 border border-gray-200 rounded-md text-sm cursor-pointer hover:bg-gray-50"
                >
                  Refresh status
                </button>
              </div>
            )}
            {xeroStatusError && (
              <p className="mt-2 text-xs text-amber-800">{xeroStatusError}</p>
            )}
          </div>
        </div>

        <div className="text-base font-semibold text-gray-900 mb-2">Company</div>

        <div className="grid grid-cols-1 gap-4">
          {FIELD_GROUPS.map((group) => (
            <div key={group.title} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">{group.title}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {group.fields.map((f) => (
                  <label key={f.key} className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600">{f.label}</span>
                    <input
                      value={form[f.key] ?? ''}
                      onChange={onChange(f.key)}
                      className="px-3 py-2 border border-gray-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder={f.label}
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <TermsAndConditionsSection />

        {org?.plan && (
          <div className="mt-4 text-xs text-gray-500">
            Plan: <span className="font-medium text-gray-700">{org.plan}</span>
          </div>
        )}
      </div>
    </div>
  );
}

